"use client";

import { useState, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  LinkAuthenticationElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import type { StripePaymentData } from "../CheckoutFlow/CheckoutFlow";
import type { SuccessData } from "../PortalManager";

/**
 * Inline styles for the portal (must work on any host page)
 */
const styles = {
  overlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  container: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "480px",
    width: "100%",
    margin: "16px",
    maxHeight: "90vh",
    overflowY: "auto" as const,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  title: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "8px",
    color: "#6b7280",
    fontSize: "24px",
    lineHeight: 1,
  },
  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  emailSection: {
    marginBottom: "8px",
  },
  paymentSection: {
    marginBottom: "16px",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 0",
    borderTop: "1px solid #e5e7eb",
    marginTop: "8px",
  },
  totalLabel: {
    fontSize: "16px",
    fontWeight: 500,
    color: "#374151",
    margin: 0,
  },
  totalAmount: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#111827",
    margin: 0,
  },
  submitButton: (primaryColor: string, disabled: boolean) => ({
    width: "100%",
    padding: "14px 24px",
    backgroundColor: disabled ? "#9ca3af" : primaryColor,
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background-color 0.2s",
    opacity: disabled ? 0.7 : 1,
  }),
  error: {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "8px",
    fontSize: "14px",
    marginTop: "8px",
  },
  processing: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    textAlign: "center" as const,
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid #e5e7eb",
    borderTopColor: "#03c2b9",
    borderRadius: "50%",
    animation: "baluu-portal-spin 1s linear infinite",
  },
  processingText: {
    marginTop: "16px",
    fontSize: "16px",
    color: "#374151",
  },
};

// Inject keyframes for spinner animation
function injectSpinnerKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("baluu-portal-spinner-keyframes")) return;
  
  const style = document.createElement("style");
  style.id = "baluu-portal-spinner-keyframes";
  style.textContent = `
    @keyframes baluu-portal-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

type StripePaymentFormProps = {
  buyerEmail?: string;
  total?: number;
  currency?: string;
  primaryColor: string;
  onSuccess: (data: SuccessData) => void;
  onClose: () => void;
  purchaseId?: string;
  courseName?: string;
  returnUrl?: string;
};

function StripePaymentFormInner({
  buyerEmail = "",
  total,
  currency = "GBP",
  primaryColor,
  onSuccess,
  onClose,
  purchaseId,
  courseName,
  returnUrl,
}: StripePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState(buyerEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError("Payment system not ready. Please try again.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();

      if (submitError) {
        setError(submitError.message || "Payment submission failed");
        setIsSubmitting(false);
        return;
      }

      setIsProcessing(true);

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          receipt_email: email,
          // Where Stripe brings the buyer back when the method has to leave
          // the page (3-D Secure off-page, bank redirects, pay-later). Without
          // one, those methods can't be confirmed at all.
          ...(returnUrl ? { return_url: returnUrl } : {}),
        },
        redirect: "if_required",
      });

      if (confirmError) {
        setError(confirmError.message || "Payment failed");
        setIsProcessing(false);
        setIsSubmitting(false);
        return;
      }

      // "succeeded" is normal auto-capture; "requires_capture" is the
      // hold-funds flow (capture_method "manual"), where the card is
      // authorized now and captured when the partner confirms. Both are a
      // completed checkout from the customer's side, and the booking is
      // created from the authorize webhook either way — the sibling surfaces
      // in packages/ui and the widget's basket checkout have handled this for
      // a while; this portal was the one that never did, so a hold-flow
      // customer was told their payment failed after it had been authorized.
      if (
        paymentIntent?.status === "succeeded" ||
        paymentIntent?.status === "requires_capture"
      ) {
        // Payment successful!
        onSuccess({
          purchaseId,
          courseName,
          total,
          currency,
          email,
          paymentIntentId: paymentIntent.id,
        });
      } else if (paymentIntent?.status === "processing") {
        // Still processing, show success anyway
        onSuccess({
          purchaseId,
          courseName,
          total,
          currency,
          email,
          paymentIntentId: paymentIntent.id,
          paymentProcessing: true,
        });
      } else {
        setError("Payment was not completed. Please try again.");
        setIsProcessing(false);
        setIsSubmitting(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsProcessing(false);
      setIsSubmitting(false);
    }
  }, [stripe, elements, email, onSuccess, purchaseId, courseName, total, currency, returnUrl]);

  const formatPrice = (amount: number, curr: string) => {
    const symbols: Record<string, string> = { GBP: "£", USD: "$", EUR: "€" };
    const symbol = symbols[curr] || curr;
    return `${symbol}${(amount / 100).toFixed(2)}`;
  };

  return (
    <div style={{ position: "relative" }}>
      {/* Processing overlay - don't unmount the form to keep PaymentElement alive */}
      {isProcessing && (
        <div style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
          borderRadius: "8px",
        }}>
          <div style={styles.spinner} />
          <p style={styles.processingText}>Processing your payment...</p>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ ...styles.form, opacity: isProcessing ? 0.3 : 1 }}>
      <div style={styles.emailSection}>
        <LinkAuthenticationElement
          options={{ defaultValues: { email: buyerEmail } }}
          onChange={(e) => setEmail(e.value.email)}
        />
      </div>

      <div style={styles.paymentSection}>
        <PaymentElement
          onReady={() => setIsReady(true)}
          options={{
            layout: "tabs",
          }}
        />
      </div>

      {total !== undefined && total > 0 && (
        <div style={styles.totalRow}>
          <p style={styles.totalLabel}>Total</p>
          <p style={styles.totalAmount}>{formatPrice(total, currency)}</p>
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      <button
        type="submit"
        disabled={!stripe || !isReady || isSubmitting}
        style={styles.submitButton(primaryColor, !stripe || !isReady || isSubmitting)}
      >
        {isSubmitting ? "Processing..." : "Pay Now"}
      </button>
    </form>
    </div>
  );
}

export type StripePaymentPortalProps = {
  isOpen: boolean;
  paymentData: StripePaymentData;
  stripePublicKey: string;
  primaryColor: string;
  /**
   * Absolute URL on this page for Stripe to return to after a redirect
   * (lib/payment-return's basketPaymentReturnUrl), and the caller handles
   * that return. Left out, redirect-only methods fail to confirm.
   */
  returnUrl?: string;
  onSuccess: (data: SuccessData) => void;
  onClose: () => void;
};

export function StripePaymentPortal({
  isOpen,
  paymentData,
  stripePublicKey,
  primaryColor,
  returnUrl,
  onSuccess,
  onClose,
}: StripePaymentPortalProps) {
  // Inject spinner keyframes
  useEffect(() => {
    injectSpinnerKeyframes();
  }, []);

  // Load Stripe with connected account if needed
  const stripePromise = useMemo(() => {
    if (!stripePublicKey || !paymentData.clientSecret) return null;
    
    const options = paymentData.paymentMethodType === "direct" && paymentData.schoolAccountID
      ? { stripeAccount: paymentData.schoolAccountID }
      : undefined;
    
    return loadStripe(stripePublicKey, options);
  }, [stripePublicKey, paymentData.clientSecret, paymentData.paymentMethodType, paymentData.schoolAccountID]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !paymentData.clientSecret || !stripePromise) return null;

  const portalContent = (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Complete Payment</h2>
          <button style={styles.closeButton} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: paymentData.clientSecret,
            appearance: {
              theme: "stripe",
              variables: {
                colorPrimary: primaryColor,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              },
            },
          }}
        >
          <StripePaymentFormInner
            buyerEmail={paymentData.buyerEmail}
            total={paymentData.total}
            currency={paymentData.currency}
            primaryColor={primaryColor}
            onSuccess={onSuccess}
            onClose={onClose}
            purchaseId={paymentData.purchaseId}
            courseName={paymentData.courseName}
            returnUrl={returnUrl}
          />
        </Elements>
      </div>
    </div>
  );

  // Render via portal to document.body (outside Shadow DOM)
  return typeof document !== "undefined"
    ? createPortal(portalContent, document.body)
    : null;
}

